/**
 * TeachingsCompletedCard Component
 * Card variant for completed rituals in the Teaching tab.
 * Matches the Figma design: image + glass overlay (checkmark, title, tags, Details)
 * + stats bar at the bottom (Views, Joined, Saved, Earnings).
 */

import {
  Banknote,
  Bookmark,
  Check,
  EllipsisVertical,
  Eye,
  MessageCircleMore,
  Upload,
  Users,
} from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { ShareModal } from "@/components/shared/ShareModal"
import { Button } from "@/components/ui/button"
import {
  useCreateRitualGroupChat,
  useOrganizerChatByRitualId,
} from "@/features/chats/hooks/useRitualGroupChat"
import { formatRitualType } from "@/features/rituals/types"
import { cn } from "@/lib"

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`
}

interface TeachingsCompletedCardProps {
  id: string
  title: string
  type: string
  participationFee: "Free" | "Paid" | "Donation-based"
  duration: number
  image?: string
  price?: number | string | null
  meetingLink?: string | null
  viewCount?: number
  joinedCount?: number
  savedCount?: number
  earnings?: number
  minimumParticipants?: number | null
  className?: string
}

function formatCount(value: number | undefined): string {
  if (value === undefined || value === 0) return "0"
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`
  return value.toString()
}

function formatEarnings(value: number | undefined): string {
  if (value === undefined || value === 0) return "$0"
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`
  return `$${value}`
}

function formatPrice(price: number | string | null | undefined): string {
  if (price === undefined || price === null || Number(price) <= 0) return ""
  return `$${Number(price).toFixed(0)}`
}

export function TeachingsCompletedCard({
  id,
  title,
  type,
  participationFee,
  duration,
  image,
  price,
  meetingLink,
  viewCount,
  joinedCount,
  savedCount,
  earnings,
  minimumParticipants,
  className,
}: TeachingsCompletedCardProps) {
  const navigate = useNavigate()
  const { data: existingGroupChat } = useOrganizerChatByRitualId(id)
  const createGroupChat = useCreateRitualGroupChat()

  const [shareOpen, setShareOpen] = useState(false)

  const handleChatWithOrganizers = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (existingGroupChat?.id) {
      navigate(`/chats/ritual/${existingGroupChat.id}`)
      return
    }
    try {
      const chat = await createGroupChat.mutateAsync(id)
      navigate(`/chats/ritual/${chat.id}`)
    } catch {
      // error handled by React Query
    }
  }

  const displayPrice = formatPrice(price)
  const typeLabel = formatRitualType(type)
  const minPartLabel =
    minimumParticipants !== undefined && minimumParticipants !== null
      ? `/${minimumParticipants}`
      : ""

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-tl-[40px] rounded-br-[40px] h-[310px] md:h-[400px] cursor-pointer flex flex-col",
        className,
      )}
      onClick={() => navigate(`/rituals/${id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          navigate(`/rituals/${id}`)
        }
      }}
    >
      {/* Image background */}
      {image ? (
        <img
          src={image}
          alt={title}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-[260px] md:h-[340px] w-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 h-[260px] md:h-[340px] bg-card" />
      )}

      <div className="absolute top-5 right-5 flex gap-3 z-50">
        <div
          className="p-3 rounded-lg bg-white/10 backdrop-blur-xs border border-gradient-glass cursor-pointer"
          onClick={handleChatWithOrganizers}
        >
          <MessageCircleMore className="w-5 h-5 text-white" />
        </div>

        <button
          type="button"
          className="p-3 rounded-lg bg-white/10 backdrop-blur-xs border border-gradient-glass cursor-pointer"
          onClick={e => {
            e.stopPropagation()
            setShareOpen(true)
          }}
          aria-label="Share"
        >
          <Upload className="w-5 h-5 text-white" />
        </button>

        <div className="p-3 rounded-lg bg-white/10 backdrop-blur-xs border border-gradient-glass ">
          <EllipsisVertical className="w-5 h-5 text-white" />
        </div>
      </div>

      {/* Glass overlay covering the image area */}
      <div className="absolute top-0 left-0 right-0 h-[260px] md:h-[340px] bg-[rgba(0,0,0,0.75)] flex flex-col items-center justify-center py-6 px-8">
        {/* Glass checkmark button — top center */}
        <div className="w-[50px] h-[50px] md:w-[70px] md:h-[70px] rounded-full border border-gradient-glass flex items-center justify-center flex-shrink-0 mb-3 md:mb-6">
          <Check className="w-[38px] h-[38px] text-white" strokeWidth={2} />
        </div>

        {/* Center: title + tags */}
        <div className="flex flex-col items-center gap-3 text-center">
          <h3 className="text-sm md:text-[18px] font-semibold text-white leading-[22px] max-w-[500px] md:mb-[11px]">
            {title}
          </h3>

          {/* Tags row */}
          <div className="flex items-center gap-2 flex-wrap justify-center mb-3 md:mb-5">
            {displayPrice && (
              <span className="text-[13px] text-white bg-brand-terracotta-bright rounded px-1.5 py-0.5">
                {displayPrice}
              </span>
            )}
            {participationFee && participationFee === "Free" && (
              <span className="text-[13px] text-white bg-brand-olive px-1 py-1 rounded-sm">
                {participationFee}
              </span>
            )}
            {participationFee && participationFee === "Donation-based" && (
              <span className="text-[13px] text-white bg-brand-green px-1 py-1 rounded-sm">
                {participationFee}
              </span>
            )}
            <span className="text-[13px] text-white">{typeLabel}</span>
            <span className="text-[13px] text-white">{formatDuration(duration)}</span>
            {meetingLink && <span className="text-[13px] text-white">@ Link</span>}
          </div>
        </div>

        {/* Reflect button */}
        <Button
          variant="outline"
          className="h-[50px] w-[180px] rounded-full border-white text-white uppercase font-semibold text-sm flex-shrink-0"
          onClick={e => {
            e.stopPropagation()
            navigate(`/rituals/${id}`)
          }}
        >
          Details
        </Button>
      </div>

      {/* Stats bar at the bottom */}
      <div className="mt-auto h-[61px] bg-white/[0.17] hidden md:flex items-center justify-between px-8 flex-shrink-0">
        <div className="flex items-center gap-6">
          <span className="text-[16px] font-medium text-white/[0.6]">
            Views · <span className="font-semibold">{formatCount(viewCount)}</span>
          </span>
          <span className="text-[16px] font-medium text-white/[0.6]">
            Joined · <span className="font-semibold">{formatCount(joinedCount)}</span>
            {minPartLabel && <span className="opacity-70">{minPartLabel}</span>}
          </span>
          <span className="text-[16px] font-medium text-white/[0.6]">
            Saved · <span className="font-semibold">{formatCount(savedCount)}</span>
          </span>
          <span className="text-[16px] font-medium text-white/[0.6]">
            Earnings · <span className="font-semibold">{formatEarnings(earnings)}</span>
          </span>
        </div>
      </div>

      <div className="mt-auto h-[50px] bg-white/[0.17] flex md:hidden items-center justify-between px-3.75 flex-shrink-0">
        <div className="flex items-center gap-6">
          <span className="text-sm text-white/[0.6] flex item-center gap-2.5">
            <Eye className="w-5 h-5" />{" "}
            <span className="text-primary">{formatCount(viewCount)}</span>
          </span>
          <span className="text-sm text-white/[0.6] flex item-center gap-2.5">
            <Users className="w-5 h-5" />{" "}
            <span>
              <span className="text-primary">{formatCount(joinedCount)}</span>
              {minPartLabel && <span className="opacity-70">{minPartLabel}</span>}
            </span>
          </span>
          <span className="text-sm text-white/[0.6] flex item-center gap-2.5">
            <Bookmark className="w-5 h-5" />{" "}
            <span className="text-primary">{formatCount(savedCount)}</span>
          </span>
          <span className="text-xs text-white/[0.6] flex item-center gap-2.5">
            <Banknote className="w-5 h-5" />{" "}
            <span className="text-primary flex items-center">{formatEarnings(earnings)}</span>
          </span>
        </div>
      </div>

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        shareUrl={`${typeof window !== "undefined" ? window.location.origin : ""}/rituals/${id}`}
        shareText={`Check out this ritual: ${title}`}
        displayPath={`/rituals/${title.toLowerCase().replace(/\s+/g, "-")}`}
      />
    </div>
  )
}
